import { track } from '../runtime/analytics'
import { createSharedStore } from '../state/create-store'
import { DEFAULT_PROMO_CODE, type PromoCode, readPromoLink, toPromoCode } from './codes'

/**
 * The visitor's promo, shared by every mount and by the page's own code
 * through `Codeverse.promo`.
 *
 * Kept in localStorage, unlike the checkout's sessionStorage draft: a test
 * group has to hold in this browser from one visit to the next, or a visitor
 * would be offered one code today and the other tomorrow, and an email given
 * last week has still earned the promo. Nothing in it is personal, only a
 * code and whether it is active. Nothing is stored until a link, a sign-up
 * or a landing page decides something, so a visitor nobody has grouped
 * follows the default.
 */
export const PROMO_KEY = 'cv.promo.v1'

export interface PromoState {
  /**
   * The visitor's offer: their test group's code, whether or not they have
   * earned it yet, or the code a sign-up form, a link or a landing page gave
   * them.
   */
  code: PromoCode
  /**
   * True once the visitor has signed up, followed a link carrying the code,
   * or reached a landing page that applies it.
   */
  active: boolean

  /**
   * The visitor signed up: the checkout applies their code from now on, or
   * `code` instead, the one the form they submitted offered.
   */
  activate(code?: PromoCode): void
}

type StoredPromo = Pick<PromoState, 'code' | 'active'>

/** What this browser has stored, validated, or the default offer, not yet active. */
function readStoredPromo(): StoredPromo {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(PROMO_KEY) ?? 'null')
    if (stored && typeof stored === 'object') {
      const { code, active } = stored as Record<string, unknown>
      const known = typeof code === 'string' ? toPromoCode(code) : undefined
      if (known) return { code: known, active: active === true }
    }
  } catch {
    // Storage throws in private-mode Safari and when it is blocked, and
    // another script may have left anything under the key.
  }
  return { code: DEFAULT_PROMO_CODE, active: false }
}

function save({ code, active }: StoredPromo): void {
  try {
    localStorage.setItem(PROMO_KEY, JSON.stringify({ code, active } satisfies StoredPromo))
  } catch {
    // The promo still holds for this page; it just won't be remembered.
  }
}

export const promo = createSharedStore<PromoState>((set, get) => ({
  ...readStoredPromo(),

  activate(code) {
    set({ code: code ?? get().code, active: true })
    save(get())
  },
}))

/** The code the checkout applies: the visitor's, once it is active. */
export function activeCode(state: StoredPromo): PromoCode | null {
  return state.active ? state.code : null
}

export const usePromo = promo.useStore

/**
 * A link applies the promo on any page, at bundle load. `?promo=` comes from
 * the email that promised the code, so it applies that code even in a browser
 * that was offered the other one, or none. `?cx=` puts the browser in a test
 * group, but never takes back a code already earned.
 */
function applyPromoLink(): void {
  const { apply, group } = readPromoLink(window.location.search)
  if (apply) promo.set({ code: apply, active: true })
  else if (group && !promo.get().active) promo.set({ code: group })
  else return
  save(promo.get())
}

applyPromoLink()

// A sign-up in another tab applies here too.
window.addEventListener('storage', (event) => {
  if (event.key === PROMO_KEY) promo.set(readStoredPromo())
})

/**
 * `Codeverse.promo`, for the page's own code: the sign-up form, what it tells
 * Klaviyo, and a landing page that offers or applies its own code.
 */
export interface PromoApi {
  /** The visitor's code, whether or not it is active yet. */
  readonly code: PromoCode
  /** Whether the checkout applies it. */
  readonly active: boolean
  /**
   * Apply a code, once the visitor has signed up: `code`, the one the form
   * they submitted offered, or their own without it. The site may show either
   * code's form, whatever the visitor's test group, so the form decides, as
   * `?promo=` does. Returns the code the checkout now applies. A code that
   * isn't one of ours changes nothing, and warns in the console.
   */
  activate(code?: string): PromoCode | null
  /**
   * Offer a code, as `?cx=` does, on a page whose offer is better than the
   * visitor's: their code becomes `code`, and signing up applies it. A visitor
   * who has already signed up has given their email, so it applies at once.
   * Returns the code the checkout now applies. A code that isn't one of ours
   * changes nothing, and warns in the console.
   */
  offer(code: string): PromoCode | null
  /**
   * Apply a code at once, as `?promo=` does, with no sign-up. Returns the
   * code the checkout now applies. A code that isn't one of ours changes
   * nothing, and warns in the console.
   */
  apply(code: string): PromoCode | null
}

/**
 * The code the page's own JavaScript passed to `promo[method]`, which may be
 * anything: one of ours, or undefined once it has warned that it isn't.
 */
function readPageCode(method: keyof PromoApi, code: unknown): PromoCode | undefined {
  const known = typeof code === 'string' ? toPromoCode(code) : undefined
  if (!known)
    console.warn(`[codeverse] promo.${method}: ${JSON.stringify(code)} is not a promo code`)
  return known
}

export const promoApi: PromoApi = {
  get code() {
    return promo.get().code
  },
  get active() {
    return promo.get().active
  },
  activate(code) {
    if (code === undefined) {
      promo.get().activate()
    } else {
      const known = readPageCode('activate', code)
      if (!known) return activeCode(promo.get())
      promo.get().activate(known)
    }
    // The page calls this once Klaviyo has the email: a sign-up, which is a
    // lead. A link's code goes through the store directly, and is not one.
    const active = activeCode(promo.get())
    if (active) track({ kind: 'lead', code: active })
    return active
  },
  // A landing page calls these two as it loads, for every visitor, so neither
  // is a sign-up, and neither is tracked as a lead.
  offer(code) {
    const known = readPageCode('offer', code)
    if (known) {
      promo.set({ code: known })
      save(promo.get())
    }
    return activeCode(promo.get())
  },
  apply(code) {
    const known = readPageCode('apply', code)
    if (known) {
      promo.set({ code: known, active: true })
      save(promo.get())
    }
    return activeCode(promo.get())
  },
}

/** Re-read storage and the URL into the store, as a fresh page load would. Used by tests. */
export function rehydratePromo(): void {
  promo.set(readStoredPromo())
  applyPromoLink()
}
